import { LeagueRanking } from "../components/LeagueRanking";
import { MathJaxFormula } from "../components/MathJaxFormula";

const seasonScoreFormula = String.raw`\(\mathrm{Score}=100000 \times r^{-0.75} \times \log_{10}\left(1+\log_{10}N\right)\)`;

export function LeaguePage() {
  return (
    <main id="top" className="league-page">
      <section
        className="league-hero-simple"
        aria-labelledby="league-hero-title"
      >
        <div className="league-shell">
          <p className="league-kicker">AIYSS / AI COMPETITION LEAGUE</p>
          <div className="league-hero-grid">
            <div className="league-hero-copy">
              <h1 id="league-hero-title">
                Kaggleでの挑戦を、
                <br />
                <span>1年間の記録に。</span>
              </h1>
              <p>Kaggleで競い、学び、継続する若者のための年間リーグ。</p>
              <div className="league-hero-actions">
                <a className="league-secondary-link" href="#how-it-works">
                  仕組みを見る <span aria-hidden="true">↓</span>
                </a>
                <a className="league-primary-link" href="/league/register/">
                  リーグに登録する <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
            <div className="league-season-card">
              <span>2026 SEASON</span>
              <strong>
                Season
                <br />
                Points
              </strong>
              <small>順位 × 継続 × 大会規模</small>
            </div>
          </div>
          <p className="league-unofficial-notice">
            <strong>非公式サービスについて</strong>
            <span>
              AIYSS AI COMPETITION
              LEAGUEは、Kaggleの公開情報を利用したAIYSS独自の非公式サービスです。KaggleおよびGoogleによる運営・公認・後援ではありません。
            </span>
          </p>
        </div>
      </section>

      <LeagueRanking />

      <section
        className="league-simple-section"
        id="season-points"
        aria-labelledby="season-points-title"
      >
        <div className="league-shell">
          <div className="league-section-top">
            <span className="league-step">02</span>
            <div>
              <p className="league-eyebrow">SEASON POINTS</p>
              <h2 id="season-points-title">2026 Season Points</h2>
            </div>
          </div>
          <div className="league-season-points-card">
            <span>SEASON POINTS</span>
            <strong>1年間の活動を記録する。</strong>
            <p>
              参加した対象コンペのScoreを、シーズンを通して合計します。多くの問題に継続して挑戦することが、そのまま記録になります。
            </p>
          </div>
        </div>
      </section>

      <section
        className="league-simple-section"
        id="how-it-works"
        aria-labelledby="how-it-works-title"
      >
        <div className="league-shell">
          <div className="league-section-top">
            <span className="league-step">03</span>
            <div>
              <p className="league-eyebrow">HOW IT WORKS</p>
              <h2 id="how-it-works-title">3つのルール</h2>
            </div>
          </div>
          <div className="league-rule-cards">
            <article>
              <b>01</b>
              <h3>順位を比べる</h3>
              <p>
                AUCやRMSEなどの生スコアではなく、コンペの最終順位を使います。
              </p>
            </article>
            <article>
              <b>02</b>
              <h3>挑戦を積み上げる</h3>
              <p>
                参加した対象コンペのポイントを、シーズンを通して合計します。
              </p>
            </article>
            <article>
              <b>03</b>
              <h3>規模は対数で補正</h3>
              <p>参加チーム数は対数で補正し、順位の評価を主役にします。</p>
            </article>
          </div>
        </div>
      </section>

      <section
        className="league-simple-section league-simple-tinted"
        id="scoring"
        aria-labelledby="scoring-title"
      >
        <div className="league-shell">
          <div className="league-section-top">
            <span className="league-step">04</span>
            <div>
              <p className="league-eyebrow">SCORING</p>
              <h2 id="scoring-title">評価の基準</h2>
            </div>
          </div>
          <div className="league-score-layout">
            <div>
              <p className="league-intro">
                参加チーム数をN、最終順位をrとします。順位はr<sup>−0.75</sup>
                、大会規模はlog<sub>10</sub>で補正します。
              </p>
              <div className="league-symbols">
                <div>
                  <strong>N</strong>
                  <span>参加チーム数</span>
                </div>
                <div>
                  <strong>r</strong>
                  <span>最終順位</span>
                </div>
                <div>
                  <strong>Σ</strong>
                  <span>対象コンペの合計</span>
                </div>
              </div>
            </div>
            <div className="league-formula-card">
              <span>1コンペあたりの計算</span>
              <MathJaxFormula
                formula={seasonScoreFormula}
                fallback="Score = 100000 × r⁻⁰·⁷⁵ × log₁₀(1 + log₁₀N)"
              />
              <small>Season Points = Σ Score</small>
            </div>
          </div>
          <p className="league-note">
            順位を強く評価し、参加チーム数は対数で穏やかに反映します。多くの問題に挑戦することにも意味があります。
          </p>
        </div>
      </section>

      <section
        className="league-simple-section league-simple-tinted"
        id="league-rules"
        aria-labelledby="league-rules-title"
      >
        <div className="league-shell">
          <div className="league-section-top">
            <span className="league-step">05</span>
            <div>
              <p className="league-eyebrow">ELIGIBILITY</p>
              <h2 id="league-rules-title">対象コンペについて</h2>
            </div>
          </div>
          <div className="league-eligibility">
            <div>
              <strong>対象</strong>
              <span>
                参加チーム数が一定数いるその年度までに終了予定のコンペ
              </span>
            </div>
            <div>
              <strong>規模</strong>
              <span>参加チーム数が1,000以上あるコンペ</span>
            </div>
          </div>
          <div className="league-bottom-actions">
            <a className="league-primary-link" href="/league/register/">
              リーグに登録する <span aria-hidden="true">→</span>
            </a>
            <a className="text-link" href="/sponsors">
              スポンサーとしてリーグを支援する <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
